import { useStore } from '@/store';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useRenderTemplate } from '@/hooks';
import { useGraphParser } from '@/api';
import { useLogicBlocks } from '@morph-mapper/node-logic';
import { createGraphBatchCapabilities } from '../lib/batch/capabilities';
import { createGraphBatchCompiler } from '../lib/batch/compiler';
import { createGraphBatchGateway } from '../lib/batch/gateway';
import { GraphBatchExecutor } from '../lib/batch/graph-batch-executor';
import { GraphBatchRunner } from '../lib/batch/runner';
import type {
  GraphBatchEvaluator,
  GraphBatchGateway,
} from '../lib/batch/contracts';
import { GraphId } from '../types';
import { iteratorKeys } from '@/config';

export const useGraphBatchProcess = () => {
  /** Global state */
  const variant = useStore(({ config }) => config.getVariant());
  const [getEntryKey, getParentId] = useStore(({ entries: e }) => [
    e.getKey,
    e.getParentId,
  ]);
  /** Local state */
  const gatewayRef = useRef<GraphBatchGateway | null>(null);
  const capabilitiesRef = useRef<
    ReturnType<typeof createGraphBatchCapabilities> | undefined
  >();
  const runnerRef = useRef<GraphBatchRunner | null>(null);
  /** Hooks */
  const { logicBlocks } = useLogicBlocks(variant);
  const { renderWithContext } = useRenderTemplate();
  /** Queries */
  const evalNode = useGraphParser();

  const resolveRenderOptions = (_: GraphId, entryId: string) => {
    const entryKey = getEntryKey(entryId);
    if (iteratorKeys.includes(entryKey)) {
      return { contextEntryId: entryId, forceItemRender: false };
    }

    const contextEntryId = getParentId(entryId);
    if (contextEntryId === undefined) {
      throw new Error('No parent: cannot determine context.');
    }

    return {
      contextEntryId,
      forceItemRender: true,
    };
  };

  // Create state management capabilities by injecting the store into the gateway.
  if (!gatewayRef.current) {
    gatewayRef.current = createGraphBatchGateway(useStore);
  }
  const gateway = gatewayRef.current;

  const nextCompiler = createGraphBatchCompiler({
    logicBlocks,
    getGraphTemplate: (graphId) => gateway.getGraphTemplate(graphId),
    renderWithContext,
    resolveRenderOptions,
  });
  const nextEvaluator: GraphBatchEvaluator = {
    evaluateWave: ({ entryId, reqs }) =>
      evalNode.mutateAsync({ entryId, reqs }),
  };

  if (!capabilitiesRef.current) {
    capabilitiesRef.current = createGraphBatchCapabilities({
      compiler: nextCompiler,
      evaluator: nextEvaluator,
    });
  }
  const capabilities = capabilitiesRef.current;

  useLayoutEffect(() => {
    capabilities.update({
      compiler: nextCompiler,
      evaluator: nextEvaluator,
    });
  });

  if (!runnerRef.current) {
    runnerRef.current = new GraphBatchRunner({
      gateway,
      createExecutor: (claim) =>
        new GraphBatchExecutor({
          claim,
          gateway,
          compiler: capabilities.compiler,
          evaluator: capabilities.evaluator,
        }),
    });
  }

  useEffect(() => {
    const runner = runnerRef.current;
    if (!runner) return;

    runner.start();
    return () => {
      runner.stop();
    };
  }, []);
};
